import { toast } from 'react-hot-toast';
import { Sparkles, Share2, Check } from 'lucide-react';

const CheckoutPlanDetails = ({
  effectiveCustomPrice,
  effectiveCustomVoucherLimit,
  isCurrentAnnualSubscriber,
  isAlreadyOnCurrentMonthlyPlan,
  isDowngradeToMonthly,
  isApprovedPendingChange,
  approvedTargetInterval,
  billingCycle,
  setBillingCycle,
  baseMonthlyPrice,
  baseAnnualPrice,
  isCustomDealCheckout,
  selectedPlan,
  formatCurrency,
  navigate
}) => {
  return (
    <>
      {/* Approved Plan Change Banner */}
      {isApprovedPendingChange && !effectiveCustomPrice && (
        <div className="p-4 sm:p-4.5 rounded-2xl bg-teal-50/80 border border-teal-200/80 text-[#067978] flex items-start justify-between gap-3 shadow-2xs">
          <div className="flex items-start gap-3">
            <Sparkles size={18} className="text-[#067978] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-xs sm:text-sm text-slate-900">
                Approved Plan Change · {selectedPlan?.name} ({approvedTargetInterval === 'year' ? 'Annual' : 'Monthly'})
              </h4>
              <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                Super Admin has approved this exact plan and billing cycle. Complete mandate authorization below to activate your switch.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Custom Deal Banner */}
      {effectiveCustomPrice && (
        <div className="p-4 sm:p-4.5 rounded-2xl bg-teal-50/80 border border-teal-200/80 text-[#067978] flex items-start justify-between gap-3 shadow-2xs">
          <div className="flex items-start gap-3">
            <Sparkles size={18} className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-xs sm:text-sm text-slate-900">
                Exclusive Custom Deal @ ₹{effectiveCustomPrice}/mo
              </h4>
              <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                This custom deal has been specially configured for your agency. Complete mandate authorization below to activate your plan.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              const shareUrl = window.location.href;
              navigator.clipboard.writeText(shareUrl);
              toast.success('Shareable payment link copied to clipboard!');
            }}
            className="px-3 py-1.5 bg-white hover:bg-slate-100 border border-teal-300 text-[#067978] font-bold text-xs rounded-xl transition-all shrink-0 flex items-center gap-1.5 cursor-pointer shadow-2xs"
            title="Share payment link with partner, accountant or co-founder"
          >
            <Share2 size={13} />
            <span>Share Link</span>
          </button>
        </div>
      )}

      {/* Step 1 · Billing Cycle */}
      <section className="rounded-2xl sm:rounded-3xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm space-y-3.5 sm:space-y-4">
        <div>
          <span className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-[#067978]">Step 1</span>
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mt-0.5">Billing cycle</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-3.5">
          {/* Monthly Option */}
          <div
            onClick={() => {
              if (isApprovedPendingChange && approvedTargetInterval === 'year') {
                toast.error('Your approved plan change is locked to Annual billing.');
                return;
              }
              if (isCurrentAnnualSubscriber) {
                toast.error('Annual plan subscribers cannot switch to Monthly directly. Please raise a request to Trrip support.');
                return;
              }
              if (isAlreadyOnCurrentMonthlyPlan) {
                toast.error('You are already subscribed to this monthly plan. Select Annual to switch your billing cycle.');
                return;
              }
              if (isDowngradeToMonthly) {
                toast.error('Downgrading to a lower monthly plan requires a request to Trrip support.');
                return;
              }
              setBillingCycle('month');
            }}
            className={`p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border transition-all flex items-center justify-between ${
              (isApprovedPendingChange && approvedTargetInterval === 'year') || isCurrentAnnualSubscriber || isAlreadyOnCurrentMonthlyPlan || isDowngradeToMonthly
                ? 'opacity-50 cursor-not-allowed border-slate-200 bg-slate-50'
                : billingCycle === 'month'
                ? 'border-[#067978] bg-teal-50/40 text-slate-900 border-2 cursor-pointer'
                : 'border-slate-200 bg-white hover:border-slate-300 cursor-pointer'
            }`}
          >
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                billingCycle === 'month' ? 'border-[#067978]' : 'border-slate-300'
              }`}>
                {billingCycle === 'month' && <div className="w-2 h-2 rounded-full bg-[#067978]" />}
              </div>
              <div>
                <span className="text-sm font-semibold text-slate-900 block">Monthly</span>
                <span className="text-xs text-slate-500 font-normal">{formatCurrency(baseMonthlyPrice)} / month + GST</span>
              </div>
            </div>
            {isApprovedPendingChange && approvedTargetInterval === 'year' ? (
              <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                Locked (Annual Only)
              </span>
            ) : (isCurrentAnnualSubscriber || isAlreadyOnCurrentMonthlyPlan || isDowngradeToMonthly) ? (
              <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                {isCurrentAnnualSubscriber ? 'Locked (Annual Plan)' : isAlreadyOnCurrentMonthlyPlan ? 'Current Active Plan' : 'Downgrade Request Required'}
              </span>
            ) : null}
          </div>

          {/* Annual Option */}
          <div
            onClick={() => {
              if (isApprovedPendingChange && approvedTargetInterval === 'month') {
                toast.error('Your approved plan change is locked to Monthly billing.');
                return;
              }
              if (isCustomDealCheckout) {
                toast.error('Custom deal plans are configured for monthly billing.');
                return;
              }
              setBillingCycle('year');
            }}
            className={`p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border transition-all flex items-center justify-between ${
              (isApprovedPendingChange && approvedTargetInterval === 'month') || isCustomDealCheckout
                ? 'opacity-50 cursor-not-allowed border-slate-200 bg-slate-50'
                : billingCycle === 'year'
                ? 'border-[#067978] bg-teal-50/40 text-slate-900 border-2 cursor-pointer'
                : 'border-slate-200 bg-white hover:border-slate-300 cursor-pointer'
            }`}
          >
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                billingCycle === 'year' ? 'border-[#067978]' : 'border-slate-300'
              }`}>
                {billingCycle === 'year' && <div className="w-2 h-2 rounded-full bg-[#067978]" />}
              </div>
              <div>
                <span className="text-sm font-semibold text-slate-900 block">Annual</span>
                <span className="text-xs text-slate-500 font-normal">{formatCurrency(baseAnnualPrice)} / year + GST</span>
              </div>
            </div>
            {isApprovedPendingChange && approvedTargetInterval === 'month' ? (
              <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                Locked (Monthly Only)
              </span>
            ) : isCustomDealCheckout ? (
              <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                Monthly Only
              </span>
            ) : (
              <span className="text-[10px] sm:text-[11px] font-semibold bg-teal-50 border border-teal-200 text-[#067978] px-2 sm:px-2.5 py-0.5 rounded-full whitespace-nowrap">
                Save 20%
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Step 2 · Selected Plan Summary */}
      <section className="rounded-2xl sm:rounded-3xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm space-y-3.5 sm:space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <span className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-[#067978]">Step 2</span>
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 mt-0.5">Your plan</h2>
          </div>
          {!isCustomDealCheckout && !isApprovedPendingChange && (
            <button
              onClick={() => navigate('/subscription')}
              className="text-xs font-semibold text-[#067978] hover:underline cursor-pointer whitespace-nowrap"
            >
              Change plan
            </button>
          )}
          {isApprovedPendingChange && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#067978] bg-teal-50 border border-teal-200 px-2.5 py-0.5 rounded-full">
              Approved Target
            </span>
          )}
        </div>

        <div className="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-slate-50/80 border border-slate-200/80 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#067978] text-white flex items-center justify-center font-bold text-base sm:text-lg shadow-2xs shrink-0">
              {selectedPlan?.name?.charAt(0) || 'G'}
            </div>
            <div className="min-w-0">
              <span className="text-sm sm:text-base font-semibold text-slate-900 block truncate">{selectedPlan?.name} Tier</span>
              <span className="text-xs text-slate-500 font-normal block leading-tight">
                Active commission wallet &amp; tools
              </span>
            </div>
          </div>
        </div>

        {/* Features List */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5 pt-2 border-t border-slate-100 text-xs text-slate-600 font-normal">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-[#067978] shrink-0 stroke-[2.5]" />
            <span>White-label traveller mobile app</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-[#067978] shrink-0 stroke-[2.5]" />
            <span>Active commission wallet</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-[#067978] shrink-0 stroke-[2.5]" />
            <span>Live location &amp; notifications</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-[#067978] shrink-0 stroke-[2.5]" />
            <span>Post Trrip Connect &amp; custom vouchers</span>
          </div>
        </div>
      </section>
    </>
  );
};

export default CheckoutPlanDetails;
